import React from 'react';
import { useAuth } from './AuthContext';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';
import { isWorkspaceHost } from '../utils/siteHosts';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

export type GeoAccessPolicy = 'FULL_ACCESS' | 'NO_ORDERS' | 'BLOCK_ALL';

type GeoAccessDecision = {
  country: string | null;
  policy: GeoAccessPolicy;
  staffBypass: boolean;
};

type GeoAccessContextValue = {
  loading: boolean;
  decision: GeoAccessDecision | null;
  /**
   * Whether the current user should be blocked from viewing the site at all.
   * This is an additional UX guard; server-side enforcement is authoritative.
   */
  blocked: boolean;
  /** Whether ordering/checkout should be disabled for the current user. */
  noOrders: boolean;
  refresh: () => Promise<void>;
};

const GeoAccessContext = React.createContext<GeoAccessContextValue>({
  loading: true,
  decision: null,
  blocked: false,
  noOrders: false,
  refresh: async () => {},
});

function isStaffType(type: string | null | undefined): boolean {
  return type === 'ADMIN' || type === 'EMPLOYEE' || type === 'SHIPPING_AGENT';
}

export function GeoAccessProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, auth } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [decision, setDecision] = React.useState<GeoAccessDecision | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5_000);
    try {
      const res = await fetch(
        applyContentLocaleToUrl(`${API_BASE}/public/geo-access`, navigator.language),
        {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        },
      );
      if (!res.ok) throw new Error('geo');
      const data = (await res.json()) as GeoAccessDecision;
      const policy: GeoAccessPolicy =
        data.policy === 'BLOCK_ALL' || data.policy === 'NO_ORDERS'
          ? data.policy
          : 'FULL_ACCESS';
      setDecision({
        country: data.country ? String(data.country) : null,
        policy,
        staffBypass: !!data.staffBypass,
      });
    } catch {
      // If the endpoint fails, default to permissive UX; backend still enforces.
      setDecision({ country: null, policy: 'FULL_ACCESS', staffBypass: true });
    } finally {
      window.clearTimeout(timeout);
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (isWorkspaceHost()) {
      setDecision({ country: null, policy: 'FULL_ACCESS', staffBypass: true });
      setLoading(false);
      return;
    }
    void refresh();
  }, [refresh]);

  const staffBypass = decision?.staffBypass && isStaffType(auth.type);
  const blocked = decision?.policy === 'BLOCK_ALL' && !staffBypass;
  const noOrders = decision?.policy === 'NO_ORDERS' && !(staffBypass && isAuthenticated);

  const value = React.useMemo<GeoAccessContextValue>(
    () => ({
      loading,
      decision,
      blocked,
      noOrders,
      refresh,
    }),
    [blocked, decision, loading, noOrders, refresh],
  );

  return <GeoAccessContext.Provider value={value}>{children}</GeoAccessContext.Provider>;
}

export function useGeoAccess(): GeoAccessContextValue {
  return React.useContext(GeoAccessContext);
}

